import React, {useState, useMemo, useEffect, useRef, useCallback} from "react"
import {useParams, useNavigate, Link} from "react-router-dom"
import {useQuery} from "react-query"
import moment from "moment"
import nprogress from "nprogress"
import swal from "sweetalert"
import classNames from "classnames"
import {toast} from "react-toastify"
import {saveAs} from "file-saver"
import {isMobile} from "react-device-detect"
import Modal from "react-bootstrap/Modal"
import useAuth from "auth/useAuth"
import useRescuers from "hooks/useRescuers"
import useAreas from "hooks/useAreas"
import useShifts from "hooks/useShifts"
import {areaDataQuery} from "hooks/areaDataQuery"
import Rescues from "components/rescue/Rescues"
import Rescuer from "components/Rescuer"
import ReportInfo from "./ReportInfo"
import ShiftDashboard from "./ShiftDashboard"
import {getReportDate, errorMessage} from "utils/utils"
import {isReportSupervisor} from "utils/report_warnings"
import "./ReportPage.css"


export default function ReportPage() {

    const {auth, API, admin, username} = useAuth()
    const {getUser, hasTag} = useRescuers()
    const navigate = useNavigate()
    const {area, date} = useParams()
    const {defaultArea, areas} = useAreas()
    const {data: shifts, isFetched: shiftsFetched, getRescuers} = useShifts()

    const showDashboard = isReportSupervisor(admin, username, shifts, hasTag)
    const [text, setText] = useState("")
    const [submitOpen, setSubmitOpen] = useState(false)
    const [rescuerCount, setRescuerCount] = useState(0)
    const [showHeadModal, setShowHeadModal] = useState(false)
    const [selectedHead, setSelectedHead] = useState(null)
    const picker = useRef()
    const isOPU = area === 'OPU'

    const shift = shifts?.[area]
    // The current shift only says something about the last few days, not about older reports
    const reportDays = moment().startOf('day').diff(moment(date), 'days')
    const isRecentDate = reportDays >= 0 && reportDays <= 2
    // Empty shift means the area is not active (off season): no dashboard, and the submit
    // form doesn't open by itself for recent days. It can still be opened with "Zaključi ponovo".
    const noActiveShift = shiftsFetched && getRescuers(area).length === 0
    const apiArea = isOPU ? '-' : area
    const {data, isLoading, refetch} = useQuery({...areaDataQuery(auth, API, apiArea, date), enabled: !!area && !!date})
    
    const reportSubmitted = !!data?.report
    const reportSent = !!data?.report?.sent

    const setArea = useCallback((area, replace=false) => {
        if (!date) navigate(encodeURI(`/report/${area}/${getReportDate()}`), {replace})
        else navigate(encodeURI(`/report/${area}/${date}`), {replace})
    }, [navigate])
    
    const setDate = useCallback((date, replace=false) => {
        navigate(encodeURI(`/report/${area}/${date}`), {replace})
    }, [area, navigate])


    useEffect(() => {
        if (!area) setArea(defaultArea, true)
        else if (!date) setDate(getReportDate(), true)
    }, [area, date, navigate])

        
    const {openRescues, pendingRescuers} = useMemo(() => {
        let openRescues = 0
        const pendingRescuers = []
        
        if (!data)
            return {openRescues, pendingRescuers}
    
        const rescuers = {}    	
    	for (let rescue of data.rescues) {
        	let username = rescue.rescuer.username
        	if (!(username in rescuers)) {
            	rescuers[username] = {
                	"rescuer": rescue.rescuer,
                	"open": 0,
                	"submitted": 0,
                	"total": 0
            	}
        	}
        	if (rescue.submitted) {
            	rescuers[username]["submitted"]++
        	}
        	else {
            	openRescues++
            	rescuers[username]["open"]++
            	rescuers[username]["pending"] = true
        	}
        	rescuers[username]["total"]++
    	}

    	
    	/* Pregled po spasiocima */
    	for (let username in rescuers) {
        	if (rescuers[username].pending)
            	pendingRescuers.push(rescuers[username])
    	}
    	    	
    	return {openRescues, pendingRescuers}
    }, [data])


    // Who an admin can pick as the signer: people recorded on this day's report, the current
    // shift heads only for recent days (it says nothing about older days), and the admin
    const {headCandidates, defaultHead} = useMemo(() => {
        const candidates = []
        if (!admin) return {headCandidates: candidates, defaultHead: null}

        const add = (rescuer, role) => {
            if (!rescuer?.username) return
            let candidate = candidates.find(c => c.rescuer.username === rescuer.username)
            if (!candidate) {
                candidate = {rescuer, roles: []}
                candidates.push(candidate)
            }
            if (!candidate.roles.includes(role)) candidate.roles.push(role)
        }

        const report = data?.report
        add(report?.head, "potpisnik")
        add(report?.rescuer, "zaključio")
        for (const event of report?.history ?? []) {
            if (event.event !== 'submit') continue
            add(event.head, "ranije potpisnik")
            add(event.rescuer, "ranije zaključio")
        }

        if (isRecentDate) {
            add(shift?.head, "vođa smene")
            const changedAt = shift?.previous_head?.changed_at
            if (changedAt && moment().diff(moment(changedAt), 'days') <= 2)
                add(shift.previous_head, "prethodni vođa smene")
        }
        add(getUser(username) ?? {username}, "ti")

        const defaultHead = report?.head?.username ?? (isRecentDate ? shift?.head?.username : null) ?? null
        return {headCandidates: candidates, defaultHead}
    }, [admin, data, isRecentDate, shift, username, getUser])

    const onSubmitClick = () => {
        if (headCandidates.length > 1) {
            setSelectedHead(defaultHead)
            setShowHeadModal(true)
        }
        else submitDailyReport(headCandidates[0]?.rescuer.username)
    }

    
    useEffect(() => {
        if (data?.report && data.report.text) {
            setText(data.report.text)
        }
        else {
            setText("")
            const isTodayBefore4pm = moment(date).isSame(moment(), 'day') && moment().hour() < 16
            const inactiveArea = noActiveShift && isRecentDate
            setSubmitOpen(data?.permissions.submit && !openRescues && !reportSubmitted && !isTodayBefore4pm && !inactiveArea)
        }

        if (data?.date) setDate(data.date)
        if (data?.report?.rescuer_count) setRescuerCount(data.report.rescuer_count)
    }, [data, openRescues, reportSubmitted, noActiveShift, isRecentDate])
    
    
    const submitDailyReport = (headUsername) => {
        if (rescuerCount === 0) {
            swal("Fali", "Morate uneti broj spasilaca", 'warning')
            return
        }

        const body = {text, "rescuer_count": rescuerCount}
        if (headUsername) body.head = headUsername
        const toaster = toast.loading("Zaključivanje dnevnog izveštaja...")
    	auth(API.reportsAreaReportDatePost, {area, date}, body).then(response => {
        	refetch()
        	toast.update(toaster, {render: "Zaključen", type: "success", isLoading: false, autoClose: 2500})
    	}).catch(response => {
        	console.log("SUBMIT ERROR", response)
        	toast.update(toaster, {render: `Greška: ${errorMessage(response)}`, type: "error", isLoading: false, autoClose: 2500})
    	})
	}
	
	const downloadDailyReport = () => {
    	nprogress.start()    
    	auth(API.reportsAreaReportDateGet, {area, date}).then(response => {
            var byteString = atob(response.data.content)
    
            // Convert that text into a byte array.
            var ab = new ArrayBuffer(byteString.length)
            var ia = new Uint8Array(ab)
            for (var i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i)
            }
            
            // Blob for saving.
            var blob = new Blob([ia], { type: "application/pdf" })
            saveAs(blob, response.data.filename)
            nprogress.done()
        }).catch(response => {
            nprogress.done()
            console.log(response)
            swal("Greška", errorMessage(response), "error")
        })
	}

    const downloadDailyFull = () => {
        nprogress.start()
       	auth(API.reportsAreaFullDateGet, {area, date}).then(response => {
            nprogress.done()
            window.location.href = response.data.url
        }).catch(response => {
            nprogress.done()
            console.log(response)
        	swal("Greška", errorMessage(response), "error")
        })
	}
	
	const sendDailyReport = () => {
    	if (!window.confirm("Potvrdi slanje dnevnog izveštaja mejlom"))
        	return
    
        const toaster = toast.loading("Slanje dnevnog izveštaja...")
    	auth(API.reportsAreaSendDatePost, {area, date}).then(response => {
            refetch()
            toast.update(toaster, {render: response.data, type: "success", isLoading: false, autoClose: 2500})
        }).catch(response => {
            const status = response?.response?.status
            if (status >= 400 && status < 500) {
                // Rejected before sending (no permission, report not found...)
                toast.update(toaster, {render: "Izveštaj nije poslat", type: "error", isLoading: false, autoClose: 2500})
                swal('Izveštaj nije poslat', errorMessage(response), 'error')
                return
            }
            // Timeout or server error: the email may have gone out anyway
            toast.update(toaster, {render: "Proverite", type: "warning", isLoading: false, autoClose: 2500})
            swal('Izveštaj je verovatno poslat', "Ali nije 100% sigurno. Proverite uskoro 'Vreme slanja' u dnevnom izveštaju. Ako je tu, trebalo bi da je sve u redu.", 'warning')
        })
	}
	
	var allClosed = !openRescues && data && data.rescues?.length > 0

	const openPicker = () => {
        picker.current.showPicker()
	}
	
    return <div className="container-fluid shadow white" id="report">
        <section className="white page-head" id="head">
            <h3>{isOPU ? "OPU" : "Dnevni izveštaj"}</h3>
            <div id="report-area-date">
                <select onChange={event => setArea(event.target.value)} value={area} id="report-area">
                    {areas.map(area => <option key={area} value={area}>{area}</option>)}
                </select>
                <div id="report-date-box">
                    <label htmlFor="report-date" onClick={openPicker}>{date === 'today' ? "Danas" : moment(date).format("Do MMMM YYYY.")}</label>
                    <input type="date" className={classNames({'mobile': isMobile})} ref={picker} value={date ?? getReportDate()} lang="sr" id="report-date" onChange={event => setDate(event.target.value)}/>
                </div>
            </div>            
        </section>
        
        {showDashboard && area && !isOPU && !noActiveShift && <ShiftDashboard area={area}/>}

        {(isLoading || !data) ? <div id="spinner"/> : <>
            <div id="first-part">     
                <section className="white" id="report-info">
                    <h3>Povredne liste</h3>
                
                    <div id="info-box">
                        {data.rescues.length ? <div>Broj povreda: {data.rescues.length}</div> : <div>Nema povreda</div>}
                        {!!openRescues && <div>Broj otvorenih povrednih listi: {openRescues}</div>}
                        {allClosed && <div>Sve povredne liste su zaključene</div>}
                    </div>
                    
                    <Rescues rescues={data.rescues}/>

                    {!isOPU && data.unassigned?.length > 0 && <>
                        <br/>
                        <h3>OPU povredne liste</h3>
                        <p className="text-muted">
                            Povredne liste koje su fizički na ovom terenu, ali ne ulaze u dnevni izveštaj za skijalište.<br/>
                            Treba proveriti da li su to zaista OPU liste, ili greškom nisu u dnevnom izveštaju.
                        </p>
                        <Rescues rescues={data.unassigned}/>
                    </>}
                </section>
                
                {!isOPU && !!openRescues && <section id="unsubmitted">
                    <div>Ne možete zaključiti dnevni izveštaj dok ima otvorenih povrednih listi. Evo ko nije zaključio:</div>
                    <div id="pending-rescuers">{pendingRescuers.map(pending => <Rescuer key={pending.rescuer.username} rescuer={pending.rescuer} onClick="profile"/>)}</div>
                </section>}
            </div>
            
            {!isOPU && <ReportInfo data={data}/>}

            {!isOPU && submitOpen && <section className="white">
                <h3>Dnevni izveštaj</h3>

                <div id="num-rescuers">
                    <label>Broj spasilaca:</label>
                    <input type="number" value={rescuerCount} min="0" onChange={e => setRescuerCount(parseInt(e.target.value))}/>
                </div>

                <div className="row">
                    <div className="col-md-6">
                        <textarea value={text} onChange={event => setText(event.target.value)} placeholder="Napomene za dnevni izveštaj..."/>
                    </div>
                </div>

                <div className="daily-buttons">
                    <button id="submit-daily-report" className="btn btn-primary" onClick={onSubmitClick} disabled={!data?.permissions.submit || !!openRescues}><i className="fas fa-file-signature"/><span>Zaključi dnevni izveštaj</span></button>
                </div>
            </section>}
            
            {!isOPU && reportSubmitted && <section className="white">
                <div className="white daily-buttons">
                    {data?.permissions.submit && !submitOpen && <button className="btn btn-secondary" onClick={e => setSubmitOpen(true)} disabled={!!openRescues}><i className="fas fa-file-signature"/><span>Zaključi ponovo</span></button>}
                    {data?.permissions.send && <button className={classNames("btn", {"btn-primary": !reportSent, "btn-secondary": reportSent})} onClick={sendDailyReport}><i className="fas fa-envelope-open-text"/><span>Pošalji izveštaj i liste</span></button>}
                    {data?.permissions.download && <button className="btn btn-primary" onClick={downloadDailyFull}><i className="fas fa-file-contract"/><span>Skini izveštaj i liste</span></button>}
                </div>
            </section>}
        </>}

        <Modal show={showHeadModal} onHide={() => setShowHeadModal(false)} id="submit-head-modal">
            <Modal.Header closeButton>
                <Modal.Title>Izaberi potpisnika</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>Ko je potpisnik (vođa smene) za ovaj dnevni izveštaj?</p>
                <div className="head-candidates">
                    {headCandidates.map(({rescuer, roles}) => <label key={rescuer.username}>
                        <input type="radio" name="head" checked={selectedHead === rescuer.username} onChange={() => setSelectedHead(rescuer.username)}/>
                        <Rescuer rescuer={rescuer}/>
                        <span className="head-roles">({roles.join(", ")})</span>
                    </label>)}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-primary" onClick={() => { setShowHeadModal(false); submitDailyReport(selectedHead) }} disabled={!selectedHead}>Zaključi</button>
                <button className="btn btn-light" onClick={() => setShowHeadModal(false)}>Nazad</button>
            </Modal.Footer>
        </Modal>
    </div>

}
